import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { TextInput, Button, Stack, Anchor, Alert, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { ForgotPasswordCommand } from '../../../application/dto/auth/ForgotPasswordCommand';
import { useAuth } from '../../hooks/useAuth';
import { PUBLIC_ROUTES } from '../../routing/routes';
import { AuthLayout } from '../../layouts/AuthLayout';

/**
 * ForgotPasswordPage - Request password reset email
 *
 * Features:
 * - Email input with validation
 * - Submit button with loading state
 * - Success message after submission
 * - Link back to login page
 * - Uses authStore for state management
 */
export const ForgotPasswordPage: React.FC = (): React.ReactElement => {
  const { forgotPassword, passwordResetState, passwordResetError } = useAuth();

  // SESSION_215: Capture form load timestamp for bot detection
  const [formLoadTime] = useState<number>(() => Date.now());

  // Mantine form with validation
  const form = useForm<ForgotPasswordCommand>({
    initialValues: {
      email: '',
      honeypot: '' // SESSION_213: Bot detection - hidden field
    },

    validate: {
      email: (value: string): string | null => {
        if (!value || value.trim().length === 0) {
          return 'Email is required';
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return 'Invalid email format';
        }
        return null;
      }
    }
  });

  /**
   * Handle form submission
   */
  const handleSubmit = async (values: ForgotPasswordCommand): Promise<void> => {
    try {
      // SESSION_215: Include form load timestamp for bot detection
      const commandWithTimestamp: ForgotPasswordCommand = {
        ...values,
        formLoadTimestamp: formLoadTime
      };

      await forgotPassword(commandWithTimestamp);
      // Success - authStore will update passwordResetState to 'emailSent'
      form.reset();
    } catch (err) {
      // Error - authStore will update passwordResetError
      // Error is already captured in authStore
    }
  };

  return (
    <AuthLayout
      title="Forgot Password"
      subtitle="Enter your email to receive a password reset link"
    >
      {/* Error Alert */}
      {passwordResetState === 'error' && passwordResetError && (
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" variant="light">
          {passwordResetError}
        </Alert>
      )}

      {/* Success Alert */}
      {passwordResetState === 'emailSent' && (
        <Alert icon={<IconCheck size={16} />} title="Email Sent" color="green" variant="light">
          If an account exists with that email, you will receive a password reset link shortly.
          Please check your inbox and spam folder.
        </Alert>
      )}

      {/* Forgot Password Form */}
      {passwordResetState !== 'emailSent' && (
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            {/* Email Input */}
            <TextInput
              label="Email"
              placeholder="your@email.com"
              type="email"
              required
              {...form.getInputProps('email')}
            />

            {/* SESSION_213: Honeypot field - hidden from users, bots will auto-fill */}
            <TextInput
              name="website_url"
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: '-9999px',
                opacity: 0,
                height: 0,
                width: 0,
                overflow: 'hidden'
              }}
              tabIndex={-1}
              autoComplete="off"
              value={form.values.honeypot || ''}
              onChange={(e): void => form.setFieldValue('honeypot', e.target.value)}
            />

            {/* Submit Button */}
            <Button type="submit" fullWidth loading={passwordResetState === 'requesting'} mt="md">
              Send Reset Link
            </Button>
          </Stack>
        </form>
      )}

      {/* Back to Login Link */}
      <Text ta="center" size="sm" mt="md">
        <Anchor component={Link} to={PUBLIC_ROUTES.LOGIN} fw={600}>
          Back to login
        </Anchor>
      </Text>
    </AuthLayout>
  );
};
